import { useEffect } from 'react';
import { HexalInstance } from '@common/enums';
import { MessageType } from '@common/types';
import { SelectTopic } from '@components/select-topic';
import { Button } from '@components/ui/button';
import { Pages } from '@constants/pages';
import useChat from '@hooks/use-chat';
import { cn, CURRENT_INSTANCE, QUERY_PARAM } from '@lib/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

function TopicPickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { topics, selectedTopic, setSelectedTopic, messages, addMessage } = useChat();

  useEffect(() => {
    if (CURRENT_INSTANCE.name !== HexalInstance.Default) {
      navigate(Pages.Chat);
    }
  }, [navigate]);

  function buildChatUrl() {
    const query = searchParams.get(QUERY_PARAM) || '';
    const params = new URLSearchParams();

    if (query) {
      params.set(QUERY_PARAM, query);
    }

    return params.toString() ? `${Pages.Chat}?${params.toString()}` : Pages.Chat;
  }

  function handleCancel() {
    navigate(buildChatUrl());
  }

  function handleTopicSelect(topicId: string) {
    const foundTopic = topics.find((topic) => topic.id === topicId);
    setSelectedTopic(topicId);

    if (foundTopic) {
      const nextMessageId = messages.reduce((maxId, message) => Math.max(maxId, message.id), 0) + 1;
      addMessage({
        id: nextMessageId,
        type: MessageType.Message,
        message: t('chat.response.topic_changed', { topic: foundTopic.displayName }),
      });
    }

    navigate(buildChatUrl());
  }

  const bgColor =
    CURRENT_INSTANCE.name === HexalInstance.Default ? 'bg-[#9898A2]' : 'bg-xircle-main';

  if (CURRENT_INSTANCE.name !== HexalInstance.Default) {
    return null;
  }

  return (
    <div className="max-h-[900px] sm:max-h-[700px] overflow-y-auto text-white" data-iframe-size="">
      <div className={`sm:w-4/5 rounded-lg p-4 mx-auto mt-2 ${bgColor}`}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold">
            {selectedTopic ? t('common.change_topic') : t('common.select_topic')}
          </h2>
          <Button
            variant="outline"
            className={cn(
              'border-white bg-white text-xircle-main hover:bg-slate-100 hover:text-xircle-main',
              CURRENT_INSTANCE.name === HexalInstance.Default && 'text-hexal-dark-text-color'
            )}
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </Button>
        </div>
        <SelectTopic topics={topics} topicGroup={null} selectTopic={handleTopicSelect} />
      </div>
    </div>
  );
}

export default TopicPickerPage;
